import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { API_BASE, resolveMediaUrl } from "../helpers/api";
import { addToCart } from "../actions/cart";

const StorePage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addingProductId, setAddingProductId] = useState(null);
  const [selectedPreviews, setSelectedPreviews] = useState({});

  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);

  useEffect(() => {
    let ignore = false;
    async function loadStoreData() {
      try {
        setLoading(true);
        setError("");
        const [categoryRes, productRes] = await Promise.all([
          fetch(`${API_BASE}/store/categories/`, { credentials: "include" }),
          fetch(`${API_BASE}/store/products/`, { credentials: "include" }),
        ]);

        if (!ignore) {
          if (!categoryRes.ok) {
            throw new Error("Failed to load categories");
          }
          if (!productRes.ok) {
            throw new Error("Failed to load products");
          }

          const categoryData = await categoryRes.json();
          const productData = await productRes.json();
          const normalizedCategories = Array.isArray(categoryData)
            ? categoryData
            : categoryData?.results || [];
          const normalizedProducts = Array.isArray(productData)
            ? productData
            : productData?.results || [];
          setCategories(normalizedCategories);
          setProducts(normalizedProducts);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || "Unable to load store data.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadStoreData();
    return () => {
      ignore = true;
    };
  }, []);

  const categoryOptions = useMemo(() => {
    const activeCategories = categories.filter((category) => category.is_active !== false);
    return [
      { label: "All", value: "all" },
      ...activeCategories.map((category) => ({
        label: category.name,
        value: category.slug,
      })),
    ];
  }, [categories]);

  const formatPrice = (item) => {
    if (item.price_display) return item.price_display;
    if (typeof item.price_cents === "number") {
      return `$${(item.price_cents / 100).toFixed(2)}`;
    }
    return "Contact us";
  };

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return products.filter((item) => {
      const categorySlug = item?.category?.slug;
      const matchesCategory =
        selectedCategory === "all" || categorySlug === selectedCategory;
      const matchesQuery = !normalizedQuery
        ? true
        : [item.name, item.description, ...(item.tags || [])]
            .join(" ")
            .toLowerCase()
            .includes(normalizedQuery);
      return matchesCategory && matchesQuery;
    });
  }, [products, query, selectedCategory]);

  const cartQuantities = useMemo(() => {
    const map = new Map();
    (cartItems || []).forEach((item) => {
      const productId = item?.product?.id;
      if (productId) {
        map.set(productId, item.quantity || 0);
      }
    });
    return map;
  }, [cartItems]);

  const handleAddToCart = async (productId) => {
    if (!productId || addingProductId) return;
    setAddingProductId(productId);
    try {
      await dispatch(addToCart(productId, 1));
    } finally {
      setAddingProductId(null);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100">
      <section className="bg-gradient-to-r from-blue-50 to-indigo-100 dark:from-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wide uppercase bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-200 rounded-full mb-3">
                New Store Experience
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
                Explore Fabrication Services & Ready-to-Order Solutions
              </h1>
              <p className="text-gray-600 dark:text-gray-300 max-w-2xl">
                Browse curated product bundles and made-to-spec services. Choose a category to filter the catalogue, or search for a specific capability.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 shadow sm:shadow-lg rounded-xl p-5 w-full md:w-80 border border-gray-100 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide mb-3">
                Need something custom?
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Tell us about your project and we&rsquo;ll quote the best fabrication pathway for you.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Request a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {categoryOptions.map((category) => (
              <button
                key={category.value}
                type="button"
                onClick={() => setSelectedCategory(category.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition ${
                  selectedCategory === category.value
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-gray-700"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search products & services"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
            <span className="absolute inset-y-0 right-3 flex items-center text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M9 4a5 5 0 100 10A5 5 0 009 4zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </div>
        </div>

        {loading ? (
          <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 px-6 py-16 text-center">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
              Loading catalogue
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Fetching the latest categories and items from the workshop.
            </p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-500/50 dark:bg-red-900/40 px-6 py-8 text-center">
            <h3 className="text-lg font-semibold text-red-700 dark:text-red-200 mb-2">{error}</h3>
            <p className="text-sm text-red-600 dark:text-red-200 mb-4">
              Please refresh or contact us if the issue persists.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              Contact support
            </a>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 px-6 py-16 text-center">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
              Nothing matched your filters just yet
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Adjust your category or search terms, or reach out for a tailored quote.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center justify-center rounded-lg border border-transparent bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Start a conversation
            </a>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {filteredProducts.map((item) => {
              const quantityInCart = cartQuantities.get(item.id) || 0;
              const isAdding = addingProductId === item.id;
              const previewImages = [
                ...(item.image ? [{ id: "primary", image: item.image, alt_text: item.name }] : []),
                ...(item.previews || []),
              ];
              const selectedIndex = Math.min(selectedPreviews[item.id] || 0, Math.max(previewImages.length - 1, 0));
              const selectedPreview = previewImages[selectedIndex];
              return (
                <article
                  key={item.id}
                  className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-lg transition"
                >
                  <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700">
                    {selectedPreview ? (
                      <img
                        src={resolveMediaUrl(selectedPreview.image)}
                        alt={selectedPreview.alt_text || item.name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-400 text-sm">
                        Image coming soon
                      </div>
                    )}
                  </div>
                  {previewImages.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                      {previewImages.map((preview, index) => (
                        <button
                          key={preview.id || index}
                          type="button"
                          onClick={() => setSelectedPreviews((current) => ({ ...current, [item.id]: index }))}
                          aria-label={`Show preview ${index + 1} of ${item.name}`}
                          className={`h-12 w-16 shrink-0 overflow-hidden rounded border-2 ${index === selectedIndex ? "border-blue-600" : "border-transparent"}`}
                        >
                          <img src={resolveMediaUrl(preview.image)} alt="" className="h-full w-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 px-2.5 py-1 text-xs font-semibold">
                        {item.category?.name || "Uncategorised"}
                      </span>
                      {item.is_digital && (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
                          Digital download
                        </span>
                      )}
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {formatPrice(item)}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {item.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">
                      {item.description}
                    </p>
                    {item.is_digital && (
                      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                        A single-use download link will be emailed after payment is confirmed.
                      </p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {item.tags?.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-6 flex items-center justify-between">
                      <a
                        href="/contact"
                        className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Enquire
                      </a>
                      <button
                        type="button"
                        onClick={() => handleAddToCart(item.id)}
                        disabled={isAdding}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                          isAdding
                            ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200"
                            : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        }`}
                      >
                        <span>
                          {isAdding
                            ? "Adding..."
                            : quantityInCart
                            ? `In cart (${quantityInCart})`
                            : "Add to cart"}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="h-4 w-4"
                          aria-hidden="true"
                        >
                          <path d="M10 2a1 1 0 011 1v6h6a1 1 0 110 2h-6v6a1 1 0 11-2 0v-6H3a1 1 0 110-2h6V3a1 1 0 011-1z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-12 grid gap-8 md:grid-cols-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Why shop with SEFF?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Local fabrication experts combining machining, metal fabrication, and on-demand 3D printing under one roof.
            </p>
          </div>
          <dl className="md:col-span-2 grid gap-6 sm:grid-cols-2">
            <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
              <dt className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Made-to-order</dt>
              <dd className="text-sm text-gray-600 dark:text-gray-300">
                Every project starts with a conversation. We adapt materials, finishing, and timelines to suit your requirements.
              </dd>
            </div>
            <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
              <dt className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Marine & industrial ready</dt>
              <dd className="text-sm text-gray-600 dark:text-gray-300">
                Trusted by boat owners, builders, and engineers for corrosion-resistant solutions and rugged hardware.
              </dd>
            </div>
            <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
              <dt className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Rapid quoting</dt>
              <dd className="text-sm text-gray-600 dark:text-gray-300">
                Send your drawings or STL files to receive quick pricing guidance and iteration support.
              </dd>
            </div>
            <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
              <dt className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Local pickup & delivery</dt>
              <dd className="text-sm text-gray-600 dark:text-gray-300">
                Flexible delivery options across SE Queensland, including onsite fitting for selected projects.
              </dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
};

export default StorePage;
